// skipcq: JS-W1028, JS-0128
import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useSchedulerSloSnapshot from '../../hooks/useSchedulerSloSnapshot';
import SchedulerScheduleStatusChart from './SchedulerScheduleStatusChart';
import SchedulerExecutionOutcomeChart from './SchedulerExecutionOutcomeChart';

export function MetricCard({ title, subtitle, footer }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ mb: subtitle ? 0.5 : 0 }}>
        {subtitle}
      </Typography>
      {footer ? (
        <Typography variant="caption" color="text.secondary">
          {footer}
        </Typography>
      ) : null}
    </Paper>
  );
}

function formatMillisLabel(milliseconds) {
  if (milliseconds === null || milliseconds === undefined) {
    return '—';
  }
  return `${milliseconds} min`;
}

function formatPercentLabel(snapshot) {
  if (!snapshot.overdueBacklogRatioLabel) {
    return '—';
  }
  const percent = Number(snapshot.overdueBacklogRatioLabel) * 100;
  if (!Number.isFinite(percent)) {
    return '—';
  }
  return `${percent.toFixed(2)}%`;
}

function SloBreachChipRow({ sloSnapshot }) {
  const breachItems = [];
  if (sloSnapshot.sloBreachLagCriticalP99 || sloSnapshot.sloBreachOverdueCritical) {
    breachItems.push('Critical threshold');
  }
  if (sloSnapshot.sloBreachLagWarningP95 || sloSnapshot.sloBreachOverdueWarning) {
    breachItems.push('Warning threshold');
  }
  return (
    <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
      {breachItems.length === 0 ? (
        <Chip size="small" color="success" label="Within published SLO envelopes" variant="outlined" />
      ) : (
        breachItems.map(function breachChip(breachLabel) {
          const chipColor = breachLabel.startsWith('Critical') ? 'error' : 'warning';
          return <Chip key={breachLabel} size="small" color={chipColor} label={breachLabel} />;
        })
      )}
    </Box>
  );
}

function SloMetricsGrid({ sloSnapshot }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Values refresh live from Firestore (<code>sloSnapshot</code>). Computation window ~
        <strong>{sloSnapshot.rollupWindowHours ?? '—'}h</strong> trailing dispatcher histograms · last computed{' '}
        <strong>
          {sloSnapshot.computedAtMillis !== null
            ? new Date(sloSnapshot.computedAtMillis).toLocaleString()
            : '—'}
        </strong>
      </Typography>
      <SloBreachChipRow sloSnapshot={sloSnapshot} />
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Dispatcher lag (estimated)"
            subtitle={`P95 ${formatMillisLabel(sloSnapshot.dispatchLagP95EstimateMinutes)}`}
            footer={`Mean ${formatMillisLabel(sloSnapshot.dispatchLagMeanEstimateMinutes)} · samples ${
              sloSnapshot.dispatchLagMergedSampleCount ?? '—'
            }`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Lag tail risk"
            subtitle={`P99 ${formatMillisLabel(sloSnapshot.dispatchLagP99EstimateMinutes)}`}
            footer={`Rolling lag streak counter ${sloSnapshot.schedulerSloLagP95BreachesConsecutiveStored ?? 0}`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Overdue backlog"
            subtitle={formatPercentLabel(sloSnapshot)}
            footer={`${sloSnapshot.overdueBeyond24hActiveCount ?? 0} overdue / ${
              sloSnapshot.activeScheduleCount ?? '—'
            } active`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Schedules snapshot"
            subtitle={`Paused ${sloSnapshot.pausedScheduleCount ?? '—'} · suppressed ${
              sloSnapshot.errorSuppressedScheduleCount ?? '—'
            }`}
            footer={`Dispatcher runs merged for lag rollup: ${sloSnapshot.rollupDispatcherRunsLoaded ?? '—'}`}
          />
        </Grid>
      </Grid>
    </>
  );
}

function OverviewTab() {
  const { sloSnapshot, loading, error } = useSchedulerSloSnapshot();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  let sloBanner = null;
  if (error) {
    sloBanner = (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Unable to read `schedulerDashboard/sloSnapshot`. Confirm global admin privileges and deployed
        `rollupSchedulerSlo`.
      </Alert>
    );
  } else if (!sloSnapshot) {
    sloBanner = (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No SLO snapshot yet — the hourly `rollupSchedulerSlo` job writes `schedulerDashboard/sloSnapshot` after the
        first successful pass.
      </Typography>
    );
  }

  return (
    <Box>
      {sloBanner}
      {error || !sloSnapshot ? null : <SloMetricsGrid sloSnapshot={sloSnapshot} />}
      <SchedulerScheduleStatusChart />
      <SchedulerExecutionOutcomeChart />
    </Box>
  );
}

export default OverviewTab;
